import { computed } from 'vue';
import { useModulesStore } from '@/stores/modules';
import { resolveMdiIcon } from '@/shared/mdi-icons';
import { i18n } from '@/i18n';
import type { ModuleManifest, ModuleFrontendBundle } from '@cavicode/network-ui';

const bundlePromises = new Map<string, Promise<ModuleFrontendBundle>>();
const BUNDLE_LOAD_TIMEOUT_MS = 15_000;

export function mergeModuleI18n(bundle: ModuleFrontendBundle): void {
    if (!bundle.i18n) return;

    for (const [locale, messages] of Object.entries(bundle.i18n)) {
        if (messages && typeof messages === 'object') {
            i18n.global.mergeLocaleMessage(locale, messages);
        }
    }
}

export async function loadModuleBundleManifest(manifest: ModuleManifest): Promise<ModuleFrontendBundle | null> {
    if (!manifest.frontendBundle) return null;

    const separator = manifest.frontendBundle.includes('?') ? '&' : '?';
    const url = `${manifest.frontendBundle}${separator}v=${encodeURIComponent(manifest.version)}`;
    const cached = bundlePromises.get(url);
    if (cached) return cached;

    let timeoutId: number | undefined;
    const promise = Promise.race([
        import(/* @vite-ignore */ url).then((mod) => mod.default as ModuleFrontendBundle),
        new Promise<never>((_, reject) => {
            timeoutId = window.setTimeout(
                () => reject(new Error(`Превышено время загрузки bundle модуля ${manifest.id}`)),
                BUNDLE_LOAD_TIMEOUT_MS
            );
        })
    ]).finally(() => {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    });

    bundlePromises.set(url, promise);
    promise.catch(() => bundlePromises.delete(url));
    return promise;
}

/**
 * Composable для работы с модульными манифестами.
 * Предоставляет реактивный доступ к загруженным модулям и их nav-entries.
 */
export function useModules() {
    const store = useModulesStore();

    const manifests = computed(() => store.manifests);
    const loaded = computed(() => store.loaded);
    const error = computed(() => store.error);

    /** Все nav-entries из всех манифестов, отсортированные по order. */
    const allNavEntries = computed(() => {
        const entries: { moduleId: string; route: string; label: string; icon: string; order: number }[] = [];
        for (const m of store.manifests) {
            for (const entry of m.navEntries) {
                entries.push({
                    moduleId: m.id,
                    route: entry.route,
                    label: entry.label,
                    icon: resolveMdiIcon(entry.icon),
                    order: entry.order ?? 100
                });
            }
        }
        return entries.sort((a, b) => a.order - b.order);
    });

    /** Все routes из всех манифестов. */
    const allRoutes = computed(() => {
        const routes: { moduleId: string; path: string; name: string; title?: string; adminOnly?: boolean }[] = [];
        for (const m of store.manifests) {
            for (const route of m.routes) {
                routes.push({
                    moduleId: m.id,
                    path: route.path,
                    name: route.name,
                    title: route.title,
                    adminOnly: route.adminOnly
                });
            }
        }
        return routes;
    });

    /** Найти манифест по id. */
    function findModule(id: string): ModuleManifest | undefined {
        return store.manifests.find((m) => m.id === id);
    }

    /**
     * Динамически загрузить frontend bundle модуля.
     * Возвращает ModuleFrontendBundle или null, если bundle не указан.
     */
    async function loadModuleBundle(moduleId: string): Promise<ModuleFrontendBundle | null> {
        const manifest = findModule(moduleId);
        if (!manifest) return null;
        return loadModuleBundleManifest(manifest);
    }

    return {
        manifests,
        loaded,
        error,
        allNavEntries,
        allRoutes,
        findModule,
        loadModuleBundle
    };
}
